<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 680 : 'auto'"
    style="z-index: 1200 !important;"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props"><slot /></div>
    </template>
    <v-form ref="form" @submit.prevent="submit">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          {{ endpoint ? $t('routes.webhookIntegrations.editEndpoint') : $t('routes.webhookIntegrations.createEndpoint') }}
          <v-btn icon variant="text" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pb-0 pt-4 scrollable">
          <v-text-field v-model="name" :label="$t('routes.webhookIntegrations.endpointName')" :rules="nameRules" variant="solo-filled" flat required />
          <v-text-field v-model="url" :label="$t('routes.webhookIntegrations.endpointUrl')" :hint="$t('routes.webhookIntegrations.endpointUrlHint')" :rules="urlRules" variant="solo-filled" flat persistent-hint required />
          <v-textarea v-model="description" :label="$t('routes.webhookIntegrations.description')" variant="solo-filled" flat rows="2" />
          <v-select v-model="state" :items="endpointStates" :label="$t('routes.webhookIntegrations.state')" variant="solo-filled" flat />

          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">{{ $t('routes.webhookIntegrations.folderRestriction') }}</div>
            <SelectNodeDialog v-model="endpointNodeId">
              <template v-slot:default="{ selectedNode }">
                <v-btn variant="outlined" color="primary">
                  <v-icon start>mdi-folder-outline</v-icon>
                  {{ selectedNode?.name || $t('routes.webhookIntegrations.selectFolderRestriction') }}
                </v-btn>
                <v-btn v-if="endpointNodeId" variant="text" class="ml-2" @click.stop="endpointNodeId = null">
                  {{ $t('routes.webhookIntegrations.clearFolderRestriction') }}
                </v-btn>
                <v-alert
                  v-if="shouldShowTeamOnlyFolderWarning(selectedNode)"
                  type="warning"
                  variant="tonal"
                  density="comfortable"
                  class="mt-3"
                  @click.stop
                >
                  {{ $t('routes.webhookIntegrations.teamOnlyFolderRestrictionWarning') }}
                </v-alert>
              </template>
            </SelectNodeDialog>
          </div>

          <v-switch
            v-model="allowTeamOnly"
            :label="$t('routes.webhookIntegrations.allowTeamOnly')"
            :hint="$t('routes.webhookIntegrations.allowTeamOnlyHint')"
            color="primary"
            persistent-hint
            inset
          />

          <WebhookEventSubscriptionsInput v-model="eventSubscriptions" :show-error="eventSubscriptionsError" />
          <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">{{ errorMessage }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn type="submit" class="bg-primary" :disabled="isSubmitting">{{ $t('routes.webhookIntegrations.save') }}</v-btn>
          <v-btn variant="text" :disabled="isSubmitting" @click="dialog = false">{{ $t('common.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { useWebhookIntegrationStore } from '@/stores/WebhookIntegrationStore.js';
import SelectNodeDialog from '@/routes/console/users/components/dialogs/SelectNodeDialog.vue';
import { getWebhookApiErrorMessage, isValidWebhookEndpointUrl } from '@/routes/console/webhook-integrations/WebhookApiError.js';
import WebhookEventSubscriptionsInput from '../WebhookEventSubscriptionsInput.vue';
import { ENDPOINT_STATES } from '../../WebhookConstants.js';

export default {
  name: 'WebhookEndpointDialog',
  components: { SelectNodeDialog, WebhookEventSubscriptionsInput },
  props: {
    modelValue: { type: Boolean, default: false },
    endpoint: { type: Object, default: null },
    nodeId: { type: String, required: true },
    integrationId: { type: String, required: true },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      activatorDialog: false,
      name: '',
      url: '',
      description: '',
      state: 'active',
      endpointNodeId: null,
      allowTeamOnly: false,
      eventSubscriptions: [],
      eventSubscriptionsError: false,
      errorMessage: '',
      isSubmitting: false,
      nameRules: [v => !!v || this.$t('routes.webhookIntegrations.nameRequired')],
      urlRules: [
        v => !!v || this.$t('routes.webhookIntegrations.urlRequired'),
        v => isValidWebhookEndpointUrl(v) || this.$t('routes.webhookIntegrations.urlInvalid'),
      ],
    };
  },
  watch: {
    dialog: {
      immediate: true,
      handler(value) {
        if (value) this.initializeForm();
      },
    },
    endpoint() {
      if (this.dialog) this.initializeForm();
    },
  },
  computed: {
    dialog: {
      get() { return this.endpoint ? this.modelValue : this.activatorDialog; },
      set(value) {
        if (this.endpoint) this.$emit('update:modelValue', value);
        else this.activatorDialog = value;
      },
    },
    endpointStates() { return ENDPOINT_STATES; },
  },
  methods: {
    initializeForm() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.name = this.endpoint?.name || '';
      this.url = this.endpoint?.url || '';
      this.description = this.endpoint?.description || '';
      this.state = this.endpoint?.state || 'active';
      this.endpointNodeId = this.endpoint?.node_id || null;
      this.allowTeamOnly = Boolean(this.endpoint?.allow_team_only);
      this.eventSubscriptions = (this.endpoint?.event_subscriptions || []).filter((eventType) => eventType !== 'webhook.test');
      this.eventSubscriptionsError = false;
      this.errorMessage = '';
      this.isSubmitting = false;
    },
    async submit() {
      const { valid } = await this.$refs.form.validate();
      this.eventSubscriptionsError = this.eventSubscriptions.length === 0;
      if (!valid || this.eventSubscriptionsError || this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        const store = useWebhookIntegrationStore();
        const payload = {
          nodeId: this.nodeId,
          integrationId: this.integrationId,
          endpointId: this.endpoint?.id,
          name: this.name,
          url: this.url,
          description: this.description,
          state: this.state,
          endpointNodeId: this.endpointNodeId,
          allowTeamOnly: this.allowTeamOnly,
          eventSubscriptions: this.eventSubscriptions,
        };
        const result = this.endpoint ? await store.updateEndpoint(payload) : await store.createEndpoint(payload);
        this.dialog = false;
        this.$emit('saved', result);
      } catch (error) {
        this.errorMessage = getWebhookApiErrorMessage(error, this.$t('routes.webhookIntegrations.endpointSaveError'));
      } finally {
        this.isSubmitting = false;
      }
    },
    isTeamOnlyNode(node) {
      return node?.teamonly === true || node?.teamonly === 1 || node?.teamonly === '1';
    },
    shouldShowTeamOnlyFolderWarning(node) {
      return this.isTeamOnlyNode(node) && !this.allowTeamOnly;
    },
  },
}
</script>
